import { Component, inject, model, OnInit, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { InputBuscador } from '../input-buscador/input-buscador';
import { ProductoApiService } from '@core/services/api/producto.service';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { Tabla } from '../tabla/tabla';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { CurrencyPipe } from '@angular/common';
import { UiDialog } from '@shared/components/ui';
import { ModalConfirmacion } from '../modal-confirmacion/modal-confirmacion';
import { ToastService } from '@shared/services/toast.service';
import { IIngresarProductoInventarioReq } from '@core/interfaces/request-response/producto.dto.interface';
import { FormsModule } from '@angular/forms';
import { AppService } from '@shared/services/app.service';
import { CategoriaApiService } from '@core/services/api/categoria-api.service';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { DeviceService } from '@shared/services/device.service';
import {
  EscanerCodigoService,
  ResultadoEscaneo,
} from '@shared/services/escaner-codigo.service';
import { CrearProducto } from '../crear-producto/crear-producto';
import { EscanerCodigo } from '../escaner-codigo/escaner-codigo';

interface IProductoConCantidad extends IProductoData {
  cantidad: number;
  descripcion_ingreso?: string;
}

@Component({
  selector: 'app-agregar-producto-inventario',
  imports: [
    FormsModule,
    InputBuscador,
    Tabla,
    CurrencyPipe,
    UiDialog,
    ModalConfirmacion,
    CrearProducto,
    EscanerCodigo,
  ],
  templateUrl: './agregar-producto-inventario.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './agregar-producto-inventario.css',
})
export class AgregarProductoInventario implements OnInit {
  // servicios
  private productoApiService = inject(ProductoApiService);
  private toastService = inject(ToastService);
  private appService = inject(AppService);
  private categoriaApiService = inject(CategoriaApiService);
  private deviceService = inject(DeviceService);
  private escanerCodigoService = inject(EscanerCodigoService);
  // datos
  searchTerm = model<string>('Buscar');
  sugerenciasProductos = signal<IProductoData[]>([]);
  productosSeleccionados = signal<IProductoConCantidad[]>([]);
  descripcionGeneral = signal('');
  usarDescripcionGeneral = signal(true);
  columnasTabla!: ITablaColumna[];
  modal_producto_sugerencias = model<boolean>(false);
  modal_confirmacion_registro = model<boolean>(false);
  // escáner / creación de producto
  scannerAbierto = signal(false);
  modal_crear_producto = model<boolean>(false);
  codigoEscaneado = signal<string | null>(null);
  stockInicialProductoCreado = signal<number | null>(null);
  categorias = signal<ICategoriaData[]>([]);

  cantidadTemplate = viewChild('cantidadTemplate');
  descripcionTemplate = viewChild('descripcionTemplate');
  inputBuscador = viewChild<InputBuscador>('inputBuscador');
  // metodos
  agregarProducto(producto: IProductoData, cantidad = 1) {
    this.productosSeleccionados.update((productos) => {
      const index = productos.findIndex((p) => p.id === producto.id);

      if (index === -1) {
        return [...productos, { ...producto, cantidad }];
      }

      return productos.map((p, i) => (i === index ? { ...p, cantidad: p.cantidad + cantidad } : p));
    });
  }
  actualizarCantidad(productoId: number, nuevaCantidad: string) {
    const cantidad = parseInt(nuevaCantidad, 10);
    if (isNaN(cantidad) || cantidad < 1) {
      return;
    }
    this.productosSeleccionados.update((productos) =>
      productos.map((p) => (p.id === productoId ? { ...p, cantidad: cantidad } : p)),
    );
  }
  eliminarProducto(productoId: number) {
    this.productosSeleccionados.update((productos) => productos.filter((p) => p.id !== productoId));
  }
  buscarProducto() {
    this.productoApiService.buscarProducto(this.searchTerm()).subscribe({
      next: (response) => {
        if (response.data.length === 0) {
          this.toastService.show(
            'top-right',
            'warn',
            'Sin resultados',
            'No se encontraron productos.',
          );
          return;
        }
        if (response.data.length === 1) {
          this.agregarProducto(response.data[0]);
          this.modal_producto_sugerencias.set(false);
          this.searchTerm.set('');
          this.inputBuscador()?.reset();
          return;
        }
        this.sugerenciasProductos.set(response.data);
        this.modal_producto_sugerencias.set(true);
      },
      error: (err) => {
        console.error('Error al buscar productos: ', err);
      },
    });
  }

  get puedeEscanearConCamara(): boolean {
    return !this.deviceService.isDesktop() && this.escanerCodigoService.soportado;
  }

  abrirEscaner() {
    if (!this.puedeEscanearConCamara) {
      this.toastService.show(
        'top-right',
        'warn',
        'Cámara no disponible',
        'Este dispositivo no permite escanear códigos con la cámara.',
      );
      return;
    }
    this.scannerAbierto.set(true);
  }

  cerrarEscaner() {
    this.scannerAbierto.set(false);
  }

  onCodigoEscaneado(evento: ResultadoEscaneo) {
    const codigo = evento.codigo.trim();
    if (!codigo) return;

    // Usamos la búsqueda existente del inventario (sin overlay global) y
    // exigimos coincidencia exacta de código para no confundir el producto
    // con otro cuyo nombre contenga el texto escaneado.
    this.productoApiService.buscarProductoSinLoader(codigo).subscribe({
      next: (response) => {
        const producto = response.data.find(
          (p) => p.activo && p.codigo.toLowerCase() === codigo.toLowerCase(),
        );

        if (producto) {
          this.agregarProducto(producto);
          this.toastService.show(
            'top-right',
            'success',
            'Producto agregado',
            `${producto.nombre} · ${producto.codigo}`,
          );
          return;
        }

        if (!/^[a-zA-Z0-9_-]{1,20}$/.test(codigo)) {
          this.toastService.show(
            'top-right',
            'warn',
            'Código no válido',
            'El código escaneado no se puede usar como código de producto (máximo 20 letras, números, guiones o guiones bajos).',
          );
          return;
        }

        this.abrirCrearProducto(codigo);
      },
      error: (err) => {
        if (err.status == 401) return;

        this.toastService.show(
          'top-right',
          'error',
          'Error al escanear',
          err.error?.message ?? 'No se pudo buscar el producto escaneado.',
        );
      },
    });
  }

  onProductoCreado(producto?: IProductoData) {
    // Leer el stock antes de cerrar el modal, porque el cierre puede disparar
    // onHide y limpiar el valor pendiente.
    const stockInicial = this.stockInicialProductoCreado();
    this.stockInicialProductoCreado.set(null);

    this.modal_crear_producto.set(false);
    this.codigoEscaneado.set(null);

    if (!producto) return;

    // El producto se creó con stock 0. Si el usuario indicó un stock en el
    // formulario, se agrega a la lista con esa cantidad para que el registro
    // de inventario haga el único incremento de stock (y su kardex).
    if (stockInicial !== null && stockInicial <= 0) {
      this.toastService.show(
        'top-right',
        'success',
        'Producto creado',
        `${producto.nombre} · ${producto.codigo}. No se agregó al ingreso porque el stock indicado es 0.`,
      );
      return;
    }

    const cantidad = stockInicial !== null ? stockInicial : 1;
    this.agregarProducto(producto, cantidad);
    this.toastService.show(
      'top-right',
      'success',
      'Producto creado y agregado',
      `${producto.nombre} · ${producto.codigo} · ${cantidad} ${
        cantidad === 1 ? 'unidad' : 'unidades'
      }`,
    );
  }

  onStockInicialCreado(stock: number) {
    this.stockInicialProductoCreado.set(stock);
  }

  cerrarCrearProducto() {
    this.modal_crear_producto.set(false);
    this.codigoEscaneado.set(null);
    this.stockInicialProductoCreado.set(null);
  }

  private abrirCrearProducto(codigo: string) {
    // El modal se abre de inmediato para no depender de ninguna carga previa.
    this.codigoEscaneado.set(codigo);
    this.scannerAbierto.set(false);
    this.modal_crear_producto.set(true);
  }

  private cargarCategorias() {
    if (this.categorias().length > 0) return;

    this.categoriaApiService.cargar().subscribe({
      next: (data) => this.categorias.set(data),
      error: (err) => {
        if (err.status == 401) return;
        // Silencioso: el producto se puede crear sin categoría.
      },
    });
  }

  onCloseModalProductoSugerencias() {
    this.modal_producto_sugerencias.set(false);
    this.sugerenciasProductos.set([]);
    this.searchTerm.set('');
    this.inputBuscador()?.reset();
  }
  // submit
  registrarProductosInventario() {
    const descGeneral = this.descripcionGeneral().trim();
    const usarGeneral = this.usarDescripcionGeneral();

    const rq: IIngresarProductoInventarioReq[] = this.productosSeleccionados().map((p) => ({
      codigo: p.codigo,
      cantidad: p.cantidad,
      concepto: usarGeneral
        ? descGeneral || undefined
        : p.descripcion_ingreso?.trim() || descGeneral || undefined,
    }));
    this.productoApiService.ingresarInventario(rq).subscribe({
      next: () => {
        this.toastService.show(
          'top-right',
          'success',
          'Inventario actualizado',
          'Los productos se han registrado en el inventario correctamente.',
        );
        this.limpiarSeleccion();
        this.modal_confirmacion_registro.set(false);
      },
      error: (err) => {
        console.error('Error al registrar productos en inventario: ', err);
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          'Ocurrió un error al registrar los productos en el inventario.',
        );
      },
    });
  }
  limpiarSeleccion() {
    this.productosSeleccionados.set([]);
    this.descripcionGeneral.set('');
  }
  //
  ngOnInit(): void {
    this.columnasTabla = [
      { key: 'nombre', titulo: 'Nombre' },
      { key: 'codigo', titulo: 'Código' },
      { key: 'precio_venta', titulo: 'Precio Venta' },
      { key: 'stock_sucursal.stock', titulo: 'Stock' },
      { key: 'cantidad', titulo: 'Cantidad', template: this.cantidadTemplate() },
      { key: 'descripcion_ingreso', titulo: 'Descripción', template: this.descripcionTemplate() },
    ];
    this.appService.setPanelHeader({
      title: 'Ingreso de Inventario',
      subtitle: 'Registra entradas de productos y actualiza el stock por sucursal.',
      showBackButton: true,
      icon: 'bi bi-box-seam',
    });
    this.cargarCategorias();
  }
}
